// 4x6 cards are postcards: their backs are authored landscape (6x4) while fronts are
// portrait, so an exact-orientation dimension match comes up empty for them.
export const isPostcardDimensions = (dimensions: Readonly<{ width: number; height: number }>): boolean => {
  return (dimensions.width === 4 && dimensions.height === 6) || (dimensions.width === 6 && dimensions.height === 4);
};

type SelectBackTemplatesArgs<T> = Readonly<{
  exactOrientation: ReadonlyArray<T>;
  rotatedOrientation: ReadonlyArray<T>;
  /** The exact-orientation query has completed (not merely returned no data yet). */
  exactQueryCompleted: boolean;
  isPostcardSize: boolean;
}>;

// Exact-orientation backs always win. Rotated backs apply only for postcard sizes, and only
// once the exact query has completed empty — while it is still loading, rotated results must
// not win (they could get pinned onto new drafts by the placeholder-default effect).
export const selectBackTemplates = <T>(args: SelectBackTemplatesArgs<T>): ReadonlyArray<T> => {
  const { exactOrientation, rotatedOrientation, exactQueryCompleted, isPostcardSize } = args;
  if (exactOrientation.length > 0) return exactOrientation;
  if (!isPostcardSize || !exactQueryCompleted) return [];
  return rotatedOrientation;
};

/**
 * Hoist the layouts a product actually includes ahead of the ones it has to sell.
 *
 * Relative order inside each group is preserved, so `TEMPLATE_ORDER` — and its blank-last rule —
 * still decides everything else. Without a predicate, or on a product where nothing is premium,
 * the list comes back in the order it went in.
 *
 * Keyed off the premium predicate rather than a second "put this one first" prop so the two
 * cannot disagree: whichever layouts the free product includes are the ones that lead the grid,
 * by construction.
 *
 * `isDefault` picks the included layout that leads the group, and so the one a new draft opens on.
 * Without it, `TEMPLATE_ORDER` decides, and making another layout free could quietly change the
 * default. It only reorders inside the included group: a default the product does not include
 * stays with the premium ones.
 */
export const orderIncludedFirst = <T>(templates: ReadonlyArray<T>, isPremium?: (template: T) => boolean, isDefault?: (template: T) => boolean): T[] => {
  if (!isPremium) return [...templates];
  const included = templates.filter(template => !isPremium(template));
  if (included.length === templates.length) return [...templates];
  const leading = isDefault ? included.filter(template => isDefault(template)) : [];
  const otherIncluded = isDefault ? included.filter(template => !isDefault(template)) : included;
  return [...leading, ...otherIncluded, ...templates.filter(template => isPremium(template))];
};
